/**
 * BulkTenantRosterCard — Iter-190 · CSV/email bulk import.
 *
 * Lets a Property Manager paste a spreadsheet column (or comma/newline
 * list) of authorized emails and POST the whole batch to
 * `POST /api/properties/{id}/account-holders/bulk`. Every accepted
 * email is flagged pre_authorized=true on the roster so when the
 * recipient self-registers they skip PENDING_PM_REVIEW and land
 * directly in the NECS Customer Service activation queue.
 */
import { useState } from 'react';
import { roleTermPluralFor } from '../lib/roleTerm';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Users, Upload, CheckCircle, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BulkTenantRosterCard({ propertyId, propertyCode, propertyName, propertyType }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!propertyId) return null;

  const previewCount = (() => {
    const rough = (text.match(/[^\s,;\n\r\t]+@[^\s,;\n\r\t]+\.[^\s,;\n\r\t]+/g) || []).length;
    return rough;
  })();

  async function onImport() {
    if (!text.trim()) {
      toast.error('Paste at least one email address to import.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/properties/${propertyId}/account-holders/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_text: text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Import failed.');
      setResult(data);
      toast.success(`${data.added} emails added to ${propertyName || 'this property'}.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      className="border-2 border-[#FFD60A]/60 bg-gradient-to-br from-[#FFFBEA] to-white p-4"
      data-testid="bulk-tenant-roster-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#FFD60A] flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-black" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#7a6300]">
              Bulk Import {roleTermPluralFor(propertyType)}
            </div>
            <div className="text-sm font-bold text-zinc-900 truncate">
              Paste a spreadsheet column of authorized emails
            </div>
            {propertyCode && (
              <div className="text-xs font-mono text-[#7a6300]">
                Roster for {propertyCode}
              </div>
            )}
          </div>
        </div>
        <Button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="bg-black text-[#FFD60A] hover:bg-zinc-800 font-bold"
          data-testid="bulk-roster-toggle"
        >
          <Upload className="h-4 w-4 mr-1" /> {open ? 'Hide importer' : 'Open importer'}
        </Button>
      </div>

      {open && (
        <div className="mt-4 space-y-3" data-testid="bulk-roster-panel">
          <Label htmlFor="bulk-roster-textarea" className="text-xs uppercase tracking-wider text-zinc-600">
            Paste emails (one per line, or comma-separated)
          </Label>
          <Textarea
            id="bulk-roster-textarea"
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`unit101@example.com\nunit102@example.com\nJane Smith <jane@example.com>`}
            className="font-mono text-sm bg-white border-zinc-300"
            data-testid="bulk-roster-textarea"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-zinc-600">
              Detected <b>{previewCount}</b> email{previewCount === 1 ? '' : 's'} in your paste.
              Duplicates and invalid entries are auto-filtered on import.
            </div>
            <Button
              type="button"
              onClick={onImport}
              disabled={busy || previewCount === 0}
              className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
              data-testid="bulk-roster-import-btn"
            >
              {busy ? 'Importing…' : `Import ${previewCount || ''} email${previewCount === 1 ? '' : 's'}`}
            </Button>
          </div>

          {result && (
            <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="bulk-roster-result">
              <div className="flex items-center gap-2 font-bold">
                <CheckCircle className="h-4 w-4" /> Roster updated
              </div>
              <ul className="mt-2 space-y-0.5 text-xs">
                <li><b>{result.added}</b> new email{result.added === 1 ? '' : 's'} added (pre-authorized)</li>
                {result.skipped_duplicate?.length > 0 && (
                  <li className="text-amber-800">
                    {result.skipped_duplicate.length} duplicate{result.skipped_duplicate.length === 1 ? '' : 's'} skipped
                  </li>
                )}
                {result.skipped_invalid?.length > 0 && (
                  <li className="text-red-800 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {result.skipped_invalid.length} invalid entr{result.skipped_invalid.length === 1 ? 'y' : 'ies'} skipped
                  </li>
                )}
                <li>Total roster size: <b>{result.roster_size}</b></li>
              </ul>
            </div>
          )}

          <div className="text-[11px] text-zinc-500 leading-snug">
            Imported emails skip the PM verification step when they self-register — you&rsquo;ve already
            vetted them here, so NECS Customer Service activates them directly.
          </div>
        </div>
      )}
    </Card>
  );
}
